import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import store from '../Store';

interface ApprovalRequest {
  id: string;
  user_id: string;
  request_type?: string;
  service_data: any;
  status: string;
  rejection_reason?: string;
  approved_by?: string;
  approved_at?: string;
  rejected_at?: string;
  created_at: string;
  updated_at: string;
  // Upgrade request fields
  service_name?: string;
  service_description?: string;
  category_id?: number;
  custom_category?: string;
  sub_category_id?: number;
  custom_sub_category?: string;
  country_id?: number;
  custom_country?: string;
  city_name?: string;
  custom_city?: string;
  website?: string;
  longitude?: number;
  latitude?: number;
  social_links?: Record<string, string>;
  working_days?: any[];
  tags?: string[];
  offerings?: string[];
  admin_notes?: string;
  package_id?: number;
  payment_completed?: boolean;
  payment_completed_at?: string;
  category?: {
    id: number;
    name: string;
    name_ar?: string;
  };
  subCategory?: {
    id: number;
    name: string;
    name_ar?: string;
  };
  countryRelation?: {
    id: number;
    name: string;
    name_ar?: string;
  };
  package?: {
    id: number;
    name: string;
    name_ar?: string;
    price: number;
    currency: string;
    duration: number;
    description?: string;
    type: string;
  };
  user: {
    id: string;
    first_name: string;
    first_name_ar: string;
    last_name: string;
    last_name_ar: string;
    email: string;
    image_path?: string;
  };
  approvedBy?: {
    id: string;
    first_name: string;
    first_name_ar: string;
  };
  logo?: string;
  images?: Array<{
    id: number;
    path: string;
  }>;
}

const AccountApprovalsPage: React.FC = observer(() => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState<ApprovalRequest | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [approvalRequests, setApprovalRequests] = useState<ApprovalRequest[]>([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [paymentForm, setPaymentForm] = useState({
    transaction_id: '',
    payment_method: '',
    notes: ''
  });

  useEffect(() => {
    fetchApprovalRequests();
  }, []);

  const fetchApprovalRequests = async () => {
    try {
      setIsProcessing(true);
      const result = await store.getAccountApprovals();
      
      if (result.success) {
        setApprovalRequests(result.data || []);
      } else {
        console.error('Error fetching approval requests:', result.message);
      }
    } catch (error) {
      console.error('Error fetching approval requests:', error);
    } finally {
      setIsProcessing(false);
    }
  };


  const filteredRequests = approvalRequests.filter(request => {
    // Handle both service_data (new_account) and service_name (upgrade) fields
    const serviceName = request.service_data?.name || request.service_name || '';
    
    const matchesSearch = 
      request.user?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.user?.first_name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      serviceName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || request.status === filterStatus;
    const matchesType = filterType === 'all' || 
      (filterType === 'new_account' && request.request_type === 'new_account') ||
      (filterType === 'upgrade' && !request.request_type);
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const handleApproveRequest = async (requestId: string, type: string) => {
    try {
      setIsProcessing(true);
      const result = await store.approveAccountRequest(requestId, type, 'Approved by admin');
      
      if (result.success) {
        await fetchApprovalRequests();
        alert('تم قبول الطلب بنجاح');
      } else {
        alert('حدث خطأ في قبول الطلب: ' + result.message);
      }
    } catch (error) {
      console.error('Error approving request:', error);
      alert('حدث خطأ في قبول الطلب');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApproveClick = (request: ApprovalRequest) => {
    const type = request.request_type || 'upgrade';
    handleApproveRequest(request.id, type);
  };

  const handleProcessPayment = async (request: ApprovalRequest) => {
    try {
      setIsProcessing(true);
      const response = await fetch(`/api/account-approvals/${request.id}/upgrade/process-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const result = await response.json();
      
      if (result.success) {
        setPaymentData(result.data);
        setSelectedRequest(request);
        setShowPaymentModal(true);
      } else {
        alert('فشل في معالجة الدفع: ' + result.message);
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('حدث خطأ أثناء معالجة الدفع');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCompletePayment = async () => {
    if (!selectedRequest || !paymentData) return;

    try {
      setIsProcessing(true);
      const response = await fetch(`/api/account-approvals/${selectedRequest.id}/upgrade/complete-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          payment_subscription_id: paymentData.payment_subscription_id,
          transaction_id: paymentForm.transaction_id,
          payment_method: paymentForm.payment_method,
          notes: paymentForm.notes
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setShowPaymentModal(false);
        setPaymentData(null);
        setPaymentForm({ transaction_id: '', payment_method: '', notes: '' });
        await fetchApprovalRequests();
        alert('تم إكمال الدفع وقبول الطلب بنجاح');
      } else {
        alert('فشل في إكمال الدفع: ' + result.message);
      }
    } catch (error) {
      console.error('Error completing payment:', error);
      alert('حدث خطأ أثناء إكمال الدفع');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedRequest || !rejectReason.trim()) {
      alert('يرجى إدخال سبب الرفض');
      return;
    }

    try {
      setIsProcessing(true);
      const type = selectedRequest.request_type || 'upgrade';
      const result = await store.rejectAccountRequest(selectedRequest.id, type, rejectReason);
      
      if (result.success) {
        await fetchApprovalRequests();
        setShowRejectModal(false);
        setRejectReason('');
        setSelectedRequest(null);
        alert('تم رفض الطلب بنجاح');
      } else {
        alert('حدث خطأ في رفض الطلب: ' + result.message);
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('حدث خطأ في رفض الطلب');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'approved':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'في الانتظار';
      case 'approved':
        return 'موافق عليه';
      case 'rejected':
        return 'مرفوض';
      default:
        return status;
    }
  };

  const getTypeText = (request: ApprovalRequest) => {
    return request.request_type === 'new_account' ? 'حساب جديد' : 'ترقية حساب';
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return '⏳';
      case 'approved':
        return '✅';
      case 'rejected':
        return '❌';
      default:
        return '❓';
    }
  };

  if (isProcessing && approvalRequests.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Calculate statistics
  const totalRequests = approvalRequests.length;
  const pendingRequests = approvalRequests.filter(request => request.status === 'pending').length;
  const approvedRequests = approvalRequests.filter(request => request.status === 'approved').length;
  const rejectedRequests = approvalRequests.filter(request => request.status === 'rejected').length;
  const newAccountRequests = approvalRequests.filter(request => request.request_type === 'new_account').length;
  const upgradeRequests = approvalRequests.filter(request => !request.request_type).length;

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">إدارة الموافقات</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">إدارة طلبات الحسابات الجديدة والترقيات</p>
          </div>
          <div className="flex items-center space-x-4 space-x-reverse">
            <button
              onClick={fetchApprovalRequests}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2 space-x-reverse"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>تحديث</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 border-l-4 border-blue-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">إجمالي الطلبات</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalRequests}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 border-l-4 border-yellow-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">في الانتظار</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{pendingRequests}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">معتمدة</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{approvedRequests}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 border-l-4 border-red-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">مرفوضة</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{rejectedRequests}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 border-l-4 border-purple-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">حسابات جديدة</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{newAccountRequests}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 border-l-4 border-indigo-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">ترقيات</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{upgradeRequests}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              البحث
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="البحث في الطلبات..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              الحالة
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">جميع الحالات</option>
              <option value="pending">في الانتظار</option>
              <option value="approved">موافق عليه</option>
              <option value="rejected">مرفوض</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              النوع
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">جميع الأنواع</option>
              <option value="new_account">حسابات جديدة</option>
              <option value="upgrade">ترقيات</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('all');
                setFilterType('all');
              }}
              className="w-full px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600 transition-colors duration-200"
            >
              إعادة تعيين
            </button>
          </div>
        </div>
      </div>

      {/* Requests List */}
      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">قائمة الطلبات</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">إجمالي {filteredRequests.length} طلب</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  رقم الطلب
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  المستخدم
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  النوع
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  الخدمة
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  الباقة
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  الحالة
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  تاريخ الطلب
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredRequests.map((request) => (
                <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    <div className="flex items-center">
                      <span className="text-lg">{getStatusIcon(request.status)}</span>
                      <span className="mr-2">#{request.id}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {request.user?.image_path && (
                        <img className="h-10 w-10 rounded-full ml-3" src={request.user.image_path} alt="" />
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {request.user?.first_name_ar || request.user?.first_name || 'Unknown'}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {request.user?.email || 'No email'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      {getTypeText(request)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white max-w-xs">
                    <div className="truncate" title={request.service_data?.name || request.service_name}>
                      {request.service_data?.name || request.service_name || 'غير محدد'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    {request.package ? (
                      <div className="flex flex-col">
                        <span className="font-medium">{request.package.name_ar || request.package.name}</span>
                        <span className="text-xs text-gray-500">
                          {request.package.price} {request.package.currency} - {request.package.duration} شهر
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400">غير محدد</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(request.status)}`}>
                      {getStatusText(request.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {request.created_at ? new Date(request.created_at).toLocaleDateString('en-US') : 'غير محدد'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2 space-x-reverse">
                      <button
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowDetailsModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 px-3 py-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900 transition-colors duration-200"
                        title="عرض التفاصيل"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      {request.status === 'pending' && (
                        <>
                          {request.package && request.package.price > 0 ? (
                            <button
                              onClick={() => handleProcessPayment(request)}
                              disabled={isProcessing}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 px-3 py-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900 transition-colors duration-200 disabled:opacity-50"
                              title="معالجة الدفع"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                              </svg>
                            </button>
                          ) : (
                            <button
                              onClick={() => handleApproveClick(request)}
                              disabled={isProcessing}
                              className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 px-3 py-1 rounded-md hover:bg-green-50 dark:hover:bg-green-900 transition-colors duration-200 disabled:opacity-50"
                              title="قبول الطلب"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowRejectModal(true);
                            }}
                            disabled={isProcessing}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 px-3 py-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900 transition-colors duration-200 disabled:opacity-50"
                            title="رفض الطلب"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredRequests.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">لا توجد طلبات</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">لا توجد طلبات مطابقة للبحث</p>
          </div>
        )}
      </div>

      {/* Request Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-5xl bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Header with Gradient */}
            <div className="relative bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 px-8 py-6">
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-4 space-x-reverse">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-1">
                  تفاصيل الطلب #{selectedRequest.id}
                </h3>
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/20 text-white border border-white/30">
                        {getTypeText(selectedRequest)}
                      </span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedRequest.status)}`}>
                        {getStatusText(selectedRequest.status)}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="p-3 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-all duration-200 backdrop-blur-sm"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-8 max-h-[70vh] overflow-y-auto">
              
              {/* Request Information Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Request ID Card */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 p-6 rounded-2xl border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3 space-x-reverse mb-3">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                        </svg>
                      </div>
                  <div>
                        <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">رقم الطلب</h4>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">#{selectedRequest.id}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* User Information Card */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-6 rounded-2xl border border-blue-200 dark:border-blue-700">
                    <div className="flex items-center space-x-4 space-x-reverse">
                      {selectedRequest.user.image_path ? (
                        <img className="h-12 w-12 rounded-xl border-2 border-white shadow-lg" src={selectedRequest.user.image_path} alt="" />
                      ) : (
                        <div className="h-12 w-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                          {(selectedRequest.user.first_name_ar || selectedRequest.user.first_name).charAt(0)}
                        </div>
                      )}
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1">المستخدم</h4>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {selectedRequest.user.first_name_ar || selectedRequest.user.first_name}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {selectedRequest.user.email}
                        </p>
                      </div>
                      </div>
                    </div>
                  </div>
                  
                {/* Right Column */}
                <div className="space-y-6">
                  {/* Date Information Card */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-6 rounded-2xl border border-green-200 dark:border-green-700">
                    <div className="flex items-center space-x-3 space-x-reverse mb-4">
                      <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                  <div>
                        <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">تاريخ الطلب</h4>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {selectedRequest.created_at ? new Date(selectedRequest.created_at).toLocaleDateString('en-US') : 'غير محدد'}
                        </p>
                      </div>
                    </div>
                    
                    {selectedRequest.approved_at && (
                      <div className="pt-3 border-t border-green-200 dark:border-green-600">
                        <p className="text-sm text-green-700 dark:text-green-300 font-medium">تاريخ الموافقة</p>
                        <p className="text-sm text-green-800 dark:text-green-200">
                          {new Date(selectedRequest.approved_at).toLocaleDateString('en-US')}
                        </p>
                      </div>
                    )}
                    
                    {selectedRequest.rejected_at && (
                      <div className="pt-3 border-t border-red-200 dark:border-red-600">
                        <p className="text-sm text-red-700 dark:text-red-300 font-medium">تاريخ الرفض</p>
                        <p className="text-sm text-red-800 dark:text-red-200">
                          {new Date(selectedRequest.rejected_at).toLocaleDateString('en-US')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Status Cards */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4 rounded-xl border border-purple-200 dark:border-purple-700">
                      <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-2">النوع</h4>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                      {getTypeText(selectedRequest)}
                    </span>
                  </div>
                  
                    <div className="bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 p-4 rounded-xl border border-yellow-200 dark:border-yellow-700">
                      <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-2">الحالة</h4>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedRequest.status)}`}>
                      {getStatusText(selectedRequest.status)}
                    </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Service Data Section */}
              <div className="mb-8">
                <div className="flex items-center space-x-3 space-x-reverse mb-6">
                  <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-6 0H9" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">بيانات الخدمة</h3>
                </div>
                
                <div className="bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 dark:from-indigo-900/20 dark:via-blue-900/20 dark:to-cyan-900/20 p-8 rounded-3xl border border-indigo-200 dark:border-indigo-700 shadow-lg">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {selectedRequest.service_name && (
                      <div className="group bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md transition-all duration-300">
                        <div className="flex items-center space-x-3 space-x-reverse mb-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">اسم الخدمة</h4>
                            <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">{selectedRequest.service_name}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {selectedRequest.service_description && (
                      <div className="group bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md transition-all duration-300">
                        <div className="flex items-center space-x-3 space-x-reverse mb-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">وصف الخدمة</h4>
                            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 leading-relaxed">{selectedRequest.service_description}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {selectedRequest.website && (
                      <div className="group bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md transition-all duration-300">
                        <div className="flex items-center space-x-3 space-x-reverse mb-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">الموقع الإلكتروني</h4>
                            <a href={selectedRequest.website} target="_blank" rel="noopener noreferrer" 
                               className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline transition-colors duration-200 mt-1 block">
                              {selectedRequest.website}
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {(selectedRequest.category || selectedRequest.custom_category) && (
                      <div className="group bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md transition-all duration-300">
                        <div className="flex items-center space-x-3 space-x-reverse mb-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">الفئة الرئيسية</h4>
                            <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                              {selectedRequest.category?.name_ar || selectedRequest.category?.name || selectedRequest.custom_category}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {(selectedRequest.subCategory || selectedRequest.custom_sub_category) && (
                      <div className="group bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md transition-all duration-300">
                        <div className="flex items-center space-x-3 space-x-reverse mb-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">الفئة الفرعية</h4>
                            <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                              {selectedRequest.subCategory?.name_ar || selectedRequest.subCategory?.name || selectedRequest.custom_sub_category}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {selectedRequest.city_name && (
                      <div className="group bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md transition-all duration-300">
                        <div className="flex items-center space-x-3 space-x-reverse mb-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">المدينة</h4>
                            <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">{selectedRequest.city_name}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Tags Section */}
                  {selectedRequest.tags && Array.isArray(selectedRequest.tags) && selectedRequest.tags.length > 0 && (
                    <div className="mt-8 bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center space-x-3 space-x-reverse mb-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                        </div>
                        <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">الخدمات الممبزة</h4>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {selectedRequest.tags.map((tag: string, index: number) => (
                          <span key={index} className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-800 dark:from-purple-900/20 dark:to-indigo-900/20 dark:text-purple-300 shadow-sm">
                            {tag}
                          </span>
                        ))}
                    </div>
                    </div>
                  )}

                  {/* Offerings Section */}
                  {selectedRequest.offerings && Array.isArray(selectedRequest.offerings) && selectedRequest.offerings.length > 0 && (
                    <div className="mt-8 bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center space-x-3 space-x-reverse mb-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                          </svg>
                        </div>
                        <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">الخدمات</h4>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {selectedRequest.offerings.map((offering: string, index: number) => (
                          <span key={index} className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 dark:from-cyan-900/20 dark:to-blue-900/20 dark:text-cyan-300 shadow-sm">
                            {offering}
                          </span>
                        ))}
                    </div>
                    </div>
                  )}

                  {selectedRequest.social_links && Object.keys(selectedRequest.social_links).length > 0 && (
                    <div className="mt-8 bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center space-x-3 space-x-reverse mb-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                          </svg>
                        </div>
                        <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">روابط التواصل الاجتماعي</h4>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {Object.entries(selectedRequest.social_links).map(([platform, url]) => (
                          <a key={platform} href={String(url)} target="_blank" rel="noopener noreferrer" 
                             className="group inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-pink-100 to-rose-100 text-pink-800 hover:from-pink-200 hover:to-rose-200 dark:from-pink-900/20 dark:to-rose-900/20 dark:text-pink-300 dark:hover:from-pink-900/30 dark:hover:to-rose-900/30 transition-all duration-200 shadow-sm hover:shadow-md">
                            <span className="mr-2">{platform}</span>
                            <svg className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        ))}
                    </div>
                    </div>
                  )}

                  {/* Working Days Section */}
                  {selectedRequest.working_days && Array.isArray(selectedRequest.working_days) && selectedRequest.working_days.length > 0 && (
                    <div className="mt-8 bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center space-x-3 space-x-reverse mb-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">أوقات العمل</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {selectedRequest.working_days.map((day: any, index: number) => (
                          <div key={index} className="flex items-center justify-between px-4 py-3 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-700">
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">{day.day || 'يوم غير محدد'}</span>
                            <span className="text-sm font-medium text-amber-700 dark:text-amber-300">{day.start} - {day.end}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Logo Section */}
                  {selectedRequest.logo && (
                    <div className="mt-8 bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center space-x-3 space-x-reverse mb-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">شعار الخدمة</h4>
                      </div>
                      <div className="flex justify-center">
                        <img 
                          src={`${process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'https://app-api.spinndo.com'}/storage/${selectedRequest.logo}`} 
                          alt="Logo" 
                          className="w-32 h-32 object-cover rounded-xl border-2 border-gray-200 dark:border-gray-700"
                        />
                      </div>
                    </div>
                  )}

                  {/* Images Section */}
                  {selectedRequest.images && Array.isArray(selectedRequest.images) && selectedRequest.images.length > 0 && (
                    <div className="mt-8 bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center space-x-3 space-x-reverse mb-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">صور الخدمة</h4>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {selectedRequest.images.map((image: any, index: number) => (
                          <div key={index} className="relative group">
                            <img 
                              src={`${process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'https://app-api.spinndo.com'}/storage/${image.path}`} 
                              alt={`Image ${index + 1}`}
                              className="w-full h-32 object-cover rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-200"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {selectedRequest.rejection_reason && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-red-700 dark:text-red-300 mb-2">
                    سبب الرفض
                  </label>
                  <div className="bg-red-50 dark:bg-red-900 p-4 rounded-lg border border-red-200 dark:border-red-700">
                    <p className="text-sm text-red-900 dark:text-red-100 whitespace-pre-wrap">{selectedRequest.rejection_reason}</p>
                  </div>
                </div>
              )}
              
              {/* Footer */}
              <div className="flex justify-end space-x-4 space-x-reverse pt-8 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="group relative px-8 py-3 text-sm font-semibold text-gray-700 bg-gradient-to-r from-gray-100 to-gray-200 border border-gray-300 rounded-2xl hover:from-gray-200 hover:to-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:from-gray-700 dark:to-gray-600 dark:text-white dark:border-gray-600 dark:hover:from-gray-600 dark:hover:to-gray-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <span className="flex items-center space-x-2 space-x-reverse">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>إغلاق</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Reject Modal */}
      {showRejectModal && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-xl bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  رفض الطلب #{selectedRequest.id}
                </h3>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                    setSelectedRequest(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  سبب الرفض *
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="يرجى كتابة سبب رفض الطلب..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {rejectReason.length}/500 حرف
                </p>
              </div>
              
              <div className="flex justify-end space-x-3 space-x-reverse">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                    setSelectedRequest(null);
                  }}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600 transition-colors duration-200"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleRejectRequest}
                  disabled={isProcessing || !rejectReason.trim()}
                  className="px-6 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {isProcessing ? 'جاري الرفض...' : 'رفض الطلب'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedRequest && paymentData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Header */}
            <div className="relative bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 px-8 py-6">
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-4 space-x-reverse">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-1">
                      معالجة الدفع
                    </h3>
                    <p className="text-white/80">طلب #{selectedRequest.id}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentData(null);
                    setPaymentForm({ transaction_id: '', payment_method: '', notes: '' });
                  }}
                  className="p-3 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-all duration-200 backdrop-blur-sm"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-8">
              {/* Package Info */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 p-6 rounded-2xl border border-gray-200 dark:border-gray-600 mb-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">تفاصيل الباقة</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">اسم الباقة</p>
                    <p className="font-medium text-gray-900 dark:text-white">{paymentData.package_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">المدة</p>
                    <p className="font-medium text-gray-900 dark:text-white">{paymentData.duration} شهر</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">المبلغ</p>
                    <p className="font-medium text-gray-900 dark:text-white">{paymentData.amount} {paymentData.currency}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">معرف الاشتراك</p>
                    <p className="font-medium text-gray-900 dark:text-white">#{paymentData.payment_subscription_id}</p>
                  </div>
                </div>
              </div>

              {/* Payment Form */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    معرف المعاملة *
                  </label>
                  <input
                    type="text"
                    value={paymentForm.transaction_id}
                    onChange={(e) => setPaymentForm({...paymentForm, transaction_id: e.target.value})}
                    placeholder="أدخل معرف المعاملة"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    طريقة الدفع *
                  </label>
                  <select
                    value={paymentForm.payment_method}
                    onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">اختر طريقة الدفع</option>
                    <option value="credit_card">بطاقة ائتمان</option>
                    <option value="bank_transfer">تحويل بنكي</option>
                    <option value="paypal">PayPal</option>
                    <option value="stripe">Stripe</option>
                    <option value="cash">نقداً</option>
                    <option value="other">أخرى</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ملاحظات (اختياري)
                  </label>
                  <textarea
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                    placeholder="أدخل أي ملاحظات إضافية..."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 space-x-reverse mt-8">
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentData(null);
                    setPaymentForm({ transaction_id: '', payment_method: '', notes: '' });
                  }}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600 transition-colors duration-200"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleCompletePayment}
                  disabled={isProcessing || !paymentForm.transaction_id || !paymentForm.payment_method}
                  className="px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {isProcessing ? 'جاري المعالجة...' : 'إكمال الدفع'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default AccountApprovalsPage;
